import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { DataTable } from '@/components/ui/data-table';
import type { Column } from '@/components/ui/data-table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Settings,
  Save,
  Loader2,
  Check,
  Sparkles,
  AlertTriangle,
} from 'lucide-react';
import { migrationV1V2Api } from './api/migration-v1-v2';
import { GlobalLabelMappingsTab } from './components/GlobalLabelMappingsTab';
import { MatiereConfigTab } from './components/MatiereConfigTab';

interface ReferentialMapping {
  id?: number;
  type: string;
  sourceId: string;
  sourceLabel: string;
  targetCode: string | null;
  targetLabel: string | null;
}

interface V2Role {
  code: string;
  label: string;
}

const v2Roles: V2Role[] = [
  { code: 'administrator', label: 'Administrateur' },
  { code: 'director', label: 'Directeur' },
  { code: 'academic_director', label: 'Directeur pédagogique' },
  { code: 'network_director', label: 'Directeur de réseau' },
  { code: 'teacher', label: 'Enseignant' },
  { code: 'student', label: 'Élève' },
  { code: 'legal_guardian', label: 'Représentant légal' },
  { code: 'supervisor', label: 'Surveillant' },
  { code: 'treasurer', label: 'Trésorier' },
  { code: 'librarian', label: 'Bibliothécaire' },
  { code: 'accountant', label: 'Comptable' },
  { code: 'executive_assistant', label: 'Assistant de direction' },
  { code: 'technical_agent', label: 'Agent technique' },
  { code: 'gardener', label: 'Jardinier' },
  { code: 'association_president', label: "Président d'association" },
  { code: 'communication_manager', label: 'Responsable communication' },
  { code: 'employer', label: 'Employeur' },
  { code: 'third_party', label: 'Tiers' },
];

/**
 * Onglet de mapping des rôles (id-keyed) : le `usertypeID` V1 est stable d'une
 * base à l'autre, donc le défaut global est indexé par ID. Comportement
 * historique inchangé.
 */
function RolesConfigTab() {
  const [mappings, setMappings] = useState<ReferentialMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [needsInit, setNeedsInit] = useState(false);
  const [initializing, setInitializing] = useState(false);

  const fetchMappings = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await migrationV1V2Api.getMappings('role');
      if (data.length === 0) {
        setNeedsInit(true);
      } else {
        setMappings(data);
        setNeedsInit(false);
      }
    } catch {
      setError('Erreur lors du chargement des mappings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMappings();
  }, [fetchMappings]);

  const handleInit = async () => {
    try {
      setInitializing(true);
      await migrationV1V2Api.seedRoles();
      await fetchMappings();
    } catch {
      setError("Erreur lors de l'initialisation");
    } finally {
      setInitializing(false);
    }
  };

  const handleTargetChange = (sourceId: string, targetCode: string | null) => {
    setMappings((prev) =>
      prev.map((m) => {
        if (m.sourceId === sourceId) {
          const v2Role = v2Roles.find((r) => r.code === targetCode);
          return { ...m, targetCode, targetLabel: v2Role?.label ?? null };
        }
        return m;
      })
    );
    setSaved(false);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      const result = await migrationV1V2Api.saveMappings('role', mappings);
      setMappings(result.mappings);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const mappedCount = mappings.filter((m) => m.targetCode).length;
  const totalCount = mappings.length;

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  const columns: Column<ReferentialMapping>[] = [
    {
      key: 'sourceId',
      header: 'ID V1',
      className: 'w-20',
      render: (m) => <span className="font-mono text-sm text-muted-foreground">{m.sourceId}</span>,
    },
    {
      key: 'sourceLabel',
      header: 'Rôle V1',
      render: (m) => <span className="font-medium text-gray-900 dark:text-gray-100">{m.sourceLabel}</span>,
    },
    {
      key: 'targetCode',
      header: 'Rôle V2',
      render: (m) => (
        <Select
          value={m.targetCode || 'unmapped'}
          onValueChange={(val) => handleTargetChange(m.sourceId, val === 'unmapped' ? null : val)}
        >
          <SelectTrigger className={!m.targetCode ? 'border-orange-300 bg-orange-50 dark:bg-orange-900/10' : ''}>
            <SelectValue placeholder="Sélectionner un rôle V2..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="unmapped">
              <span className="text-gray-400">— Non mappé —</span>
            </SelectItem>
            {v2Roles.map((role) => (
              <SelectItem key={role.code} value={role.code}>
                <span className="flex items-center gap-2">
                  <span className="font-mono text-xs text-gray-400">{role.code}</span>
                  <span>{role.label}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ),
    },
    {
      key: 'status',
      header: 'Statut',
      className: 'w-20 text-center',
      render: (m) =>
        m.targetCode ? (
          <Check className="h-4 w-4 text-green-500 mx-auto" />
        ) : (
          <AlertTriangle className="h-4 w-4 text-orange-400 mx-auto" />
        ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Mapping des rôles V1 → V2, appliqué à toutes les écoles (surchargeable par école).
        </p>
        <div className="flex items-center gap-3 shrink-0">
          {totalCount > 0 && (
            <Badge variant={mappedCount === totalCount ? 'success' : 'warning'}>
              {mappedCount}/{totalCount} mappés
            </Badge>
          )}
          {mappings.length > 0 && (
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : saved ? (
                <Check className="h-4 w-4" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {saved ? 'Sauvegardé' : 'Enregistrer'}
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">{error}</div>
      )}

      {needsInit ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <Sparkles className="h-10 w-10 text-violet-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                Les mappings de rôles n'ont pas encore été initialisés.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                L'initialisation va créer les 16 rôles V1 avec un pré-mapping automatique vers les rôles V2.
              </p>
              <Button onClick={handleInit} disabled={initializing}>
                {initializing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                Initialiser les rôles
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <DataTable
          columns={columns}
          data={mappings}
          loading={loading}
          emptyMessage="Aucun mapping configuré"
          keyExtractor={(m) => m.sourceId}
        />
      )}
    </div>
  );
}

/**
 * Destinations V2 possibles pour un champ natif V1 (`ef_*`). Miroir de
 * `MigrationConfigController::NATIVE_FIELD_TARGETS` côté backend.
 */
const nativeTargets: V2Role[] = [
  { code: 'firstname', label: 'Prénom' },
  { code: 'lastname', label: 'Nom' },
  { code: 'email', label: 'Email' },
  { code: 'phone', label: 'Téléphone mobile' },
  { code: 'phone_fixe', label: 'Téléphone fixe' },
  { code: 'phone_pro', label: 'Téléphone professionnel' },
  { code: 'birthdate', label: 'Date de naissance' },
  { code: 'gender', label: 'Sexe' },
  { code: 'civility', label: 'Civilité' },
  { code: 'profession', label: 'Profession' },
  { code: 'address', label: 'Adresse' },
  { code: 'photo', label: 'Photo' },
  { code: 'country', label: 'Pays' },
  { code: 'blood_group', label: 'Groupe sanguin' },
  { code: 'ine', label: 'INE' },
  { code: 'register_number', label: "Numéro d'inscription" },
  { code: 'birth_place', label: 'Lieu de naissance' },
  { code: 'entry_date', label: "Date d'entrée (UserRole)" },
  { code: 'exit_date', label: 'Date de sortie (UserRole)' },
  { code: 'withdrawal_date', label: 'Date de désistement (UserRole)' },
  { code: 'remarks', label: 'Remarques' },
  { code: 'handled_by_step', label: 'Géré par une autre étape' },
  { code: 'not_migrated', label: 'Non migré' },
];

/**
 * Décompose la clé d'un champ natif `{form}:{code}` (ex. `1:ef_email`) en
 * formulaire (1 = élève, 2 = parent) + code `ef_*`. Tolère l'absence de préfixe.
 */
function parseNativeSource(sourceId: string): { form: number | null; code: string } {
  const idx = sourceId.indexOf(':');
  if (idx === -1) return { form: null, code: sourceId };
  const form = Number(sourceId.slice(0, idx));
  return { form: Number.isNaN(form) ? null : form, code: sourceId.slice(idx + 1) };
}

const nativeRoleLabel = (form: number | null): string =>
  form === 1 ? 'Élève' : form === 2 ? 'Responsable légal' : '—';

/**
 * Onglet de mapping des champs natifs École Futée (`ef_*`) vers leur
 * destination V2. **Dissocié par formulaire** (clé `{form}:{code}`) : form 1
 * → user_role `student`, form 2 → `legal_guardian`. Un code partagé (email,
 * téléphone, photo, adresse…) apparaît donc une fois par rôle. Id-keyed et
 * global (le code V1 est stable inter-écoles). Même mécanique que les rôles.
 */
function NativeFieldsConfigTab({ form }: { form: 1 | 2 }) {
  const [mappings, setMappings] = useState<ReferentialMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [needsInit, setNeedsInit] = useState(false);
  const [initializing, setInitializing] = useState(false);

  const fetchMappings = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await migrationV1V2Api.getMappings('native_field');
      // Le référentiel est au nouveau format `{form}:code`. Si seules des lignes
      // héritées (sans préfixe) existent, on propose la (ré)initialisation.
      const hasNewFormat = data.some((m) => m.sourceId.includes(':'));
      setNeedsInit(!hasNewFormat);
      setMappings(hasNewFormat ? data : []);
    } catch {
      setError('Erreur lors du chargement des champs natifs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMappings();
  }, [fetchMappings]);

  const handleInit = async () => {
    try {
      setInitializing(true);
      await migrationV1V2Api.seedNativeFields();
      await fetchMappings();
    } catch {
      setError("Erreur lors de l'initialisation");
    } finally {
      setInitializing(false);
    }
  };

  const handleTargetChange = (sourceId: string, targetCode: string | null) => {
    setMappings((prev) =>
      prev.map((m) => {
        if (m.sourceId === sourceId) {
          const target = nativeTargets.find((t) => t.code === targetCode);
          return { ...m, targetCode, targetLabel: target?.label ?? null };
        }
        return m;
      })
    );
    setSaved(false);
  };

  // Lignes du formulaire courant uniquement (élève OU parent).
  const formMappings = mappings.filter((m) => parseNativeSource(m.sourceId).form === form);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      // On n'enregistre que les lignes du formulaire courant (upsert par
      // sourceId — les lignes de l'autre formulaire ne sont pas touchées).
      const result = await migrationV1V2Api.saveMappings('native_field', formMappings);
      const savedById = new Map(result.mappings.map((m) => [m.sourceId, m]));
      setMappings((prev) => prev.map((m) => savedById.get(m.sourceId) ?? m));
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      setError('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const mappedCount = formMappings.filter((m) => m.targetCode && m.targetCode !== 'not_migrated').length;
  const totalCount = formMappings.length;

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  const sortedMappings = [...formMappings].sort((a, b) => a.sourceLabel.localeCompare(b.sourceLabel));

  const columns: Column<ReferentialMapping>[] = [
    {
      key: 'sourceId',
      header: 'Code V1',
      className: 'w-52',
      render: (m) => (
        <span className="font-mono text-xs text-muted-foreground">{parseNativeSource(m.sourceId).code}</span>
      ),
    },
    {
      key: 'sourceLabel',
      header: 'Champ V1',
      render: (m) => <span className="font-medium text-gray-900 dark:text-gray-100">{m.sourceLabel}</span>,
    },
    {
      key: 'targetCode',
      header: 'Destination V2',
      render: (m) => (
        <Select
          value={m.targetCode || 'unmapped'}
          onValueChange={(val) => handleTargetChange(m.sourceId, val === 'unmapped' ? null : val)}
        >
          <SelectTrigger className={!m.targetCode ? 'border-orange-300 bg-orange-50 dark:bg-orange-900/10' : ''}>
            <SelectValue placeholder="Sélectionner une destination V2..." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="unmapped">
              <span className="text-gray-400">— Non défini —</span>
            </SelectItem>
            {nativeTargets.map((t) => (
              <SelectItem key={t.code} value={t.code}>
                <span className="flex items-center gap-2">
                  <span className="font-mono text-xs text-gray-400">{t.code}</span>
                  <span>{t.label}</span>
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ),
    },
    {
      key: 'status',
      header: 'Statut',
      className: 'w-20 text-center',
      render: (m) =>
        m.targetCode && m.targetCode !== 'not_migrated' ? (
          <Check className="h-4 w-4 text-green-500 mx-auto" />
        ) : (
          <AlertTriangle className="h-4 w-4 text-orange-400 mx-auto" />
        ),
    },
  ];

  const roleLabel = nativeRoleLabel(form);
  const roleTarget = form === 1 ? 'student' : 'legal_guardian';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Champs natifs École Futée (<code>ef_*</code>) de la fiche <strong>{roleLabel.toLowerCase()}</strong> →
          destination V2 (user_role <code>{roleTarget}</code>), appliqué à toutes les écoles. Les champs
          relationnels (classe, niveau, groupe…) sont « gérés par une autre étape » ; l'adresse est migrée
          nativement (composite géocodé).
        </p>
        <div className="flex items-center gap-3 shrink-0">
          {totalCount > 0 && (
            <Badge variant={mappedCount === totalCount ? 'success' : 'warning'}>
              {mappedCount}/{totalCount} mappés
            </Badge>
          )}
          {formMappings.length > 0 && (
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : saved ? (
                <Check className="h-4 w-4" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              {saved ? 'Sauvegardé' : 'Enregistrer'}
            </Button>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700">{error}</div>
      )}

      {needsInit ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <Sparkles className="h-10 w-10 text-violet-400 mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                Le référentiel des champs natifs n'a pas encore été initialisé.
              </p>
              <p className="text-sm text-slate-500 mb-6">
                L'initialisation crée le catalogue des champs <code>ef_*</code> (élèves et parents) avec un mapping par défaut vers les destinations V2.
              </p>
              <Button onClick={handleInit} disabled={initializing}>
                {initializing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4" />
                )}
                Initialiser les champs natifs
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <DataTable
          columns={columns}
          data={sortedMappings}
          loading={loading}
          emptyMessage="Aucun champ natif configuré"
          keyExtractor={(m) => m.sourceId}
        />
      )}
    </div>
  );
}

export function ConfigPage() {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-indigo-600 text-white">
          <Settings className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">
            Configuration
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Mappings des référentiels V1 → V2 (défauts globaux pour toutes les écoles)
          </p>
        </div>
      </div>

      <Tabs defaultValue="role">
        <TabsList>
          <TabsTrigger value="role">Rôles</TabsTrigger>
          <TabsTrigger value="cycle">Cycles</TabsTrigger>
          <TabsTrigger value="niveau">Niveaux</TabsTrigger>
          <TabsTrigger value="matiere">Matières</TabsTrigger>
          <TabsTrigger value="native_student">Champs natifs élèves</TabsTrigger>
          <TabsTrigger value="native_parent">Champs natifs parents</TabsTrigger>
        </TabsList>

        <TabsContent value="role">
          <RolesConfigTab />
        </TabsContent>
        <TabsContent value="cycle">
          <GlobalLabelMappingsTab type="cycle" v2Resource="cycles" v2Label="Cycle V2" noun="cycle" />
        </TabsContent>
        <TabsContent value="niveau">
          <GlobalLabelMappingsTab type="niveau" v2Resource="niveaux" v2Label="Niveau V2" noun="niveau" enCodeKind="mef" />
        </TabsContent>
        <TabsContent value="matiere">
          <MatiereConfigTab />
        </TabsContent>
        <TabsContent value="native_student">
          <NativeFieldsConfigTab form={1} />
        </TabsContent>
        <TabsContent value="native_parent">
          <NativeFieldsConfigTab form={2} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
